// Per-widget settings store with selector-based subscriptions.
//
// The original implementation used a plain React Context — any setting
// change re-rendered every widget consuming the context. With 15+ widgets
// per tab and the coin picker being a frequent click target, that meant a
// big chunk of the canvas flashing on every coin change.
//
// We now expose the store imperatively via a context-held ref bundle and
// subscribe per (widgetId, key) via useSyncExternalStore. Each widget
// re-renders ONLY when its own setting changes. Same external API as
// before — drop-in replacement for useWidgetSetting consumers.

import React, { createContext, useContext, useRef, useEffect, useCallback, useSyncExternalStore } from 'react';

const CockpitContext = createContext(null);
// Read-only context for the current edit state. Separate from the settings
// context so toggling Edit/Lock doesn't re-render every settings consumer.
const CockpitEditContext = createContext(false);

export function useCockpitEditing() {
  return useContext(CockpitEditContext);
}

export function CockpitProvider({ widgetSettings, setWidgetSetting, editing = false, children }) {
  // Live refs so the listener API stays stable across re-renders.
  const settingsRef = useRef(widgetSettings);
  settingsRef.current = widgetSettings;
  const setterRef = useRef(setWidgetSetting);
  setterRef.current = setWidgetSetting;

  // Per-(id,key) listener map. A widget that subscribes to
  // settings['chart#1']['coin'] gets called only when that pair changes.
  const listenersRef = useRef(new Map());

  // Fan out: when widgetSettings prop changes, diff and notify only the
  // listeners whose slice changed. Early return when nothing changed.
  const prevRef = useRef(widgetSettings);
  useEffect(() => {
    const prev = prevRef.current;
    const next = widgetSettings;
    if (prev === next) return;
    prevRef.current = next;
    const map = listenersRef.current;
    for (const [listenerKey, fns] of map) {
      const [widgetId, key] = listenerKey.split('::');
      const prevVal = prev?.[widgetId]?.[key];
      const nextVal = next?.[widgetId]?.[key];
      if (prevVal === nextVal) continue;
      for (const fn of [...fns]) {
        try { fn(); } catch { /* listener errors should not block fan-out */ }
      }
    }
  }, [widgetSettings]);

  const ctxRef = useRef(null);
  if (!ctxRef.current) {
    ctxRef.current = {
      subscribe(widgetId, key, listener) {
        const map = listenersRef.current;
        const k = `${widgetId}::${key}`;
        let set = map.get(k);
        if (!set) { set = new Set(); map.set(k, set); }
        set.add(listener);
        return () => {
          set.delete(listener);
          if (set.size === 0) map.delete(k);
        };
      },
      getSnapshot(widgetId, key) {
        return settingsRef.current?.[widgetId]?.[key];
      },
      setValue(widgetId, key, value) {
        setterRef.current?.(widgetId, key, value);
      },
    };
  }

  return (
    <CockpitContext.Provider value={ctxRef.current}>
      <CockpitEditContext.Provider value={editing}>
        {children}
      </CockpitEditContext.Provider>
    </CockpitContext.Provider>
  );
}

export function useWidgetSetting(widgetId, key, defaultValue) {
  const ctx = useContext(CockpitContext);

  // Selector-style subscription: only this (widgetId, key) combo triggers
  // re-render when its value changes. Widgets reading unrelated settings
  // stay quiescent.
  const subscribe = useCallback(
    (cb) => (ctx ? ctx.subscribe(widgetId, key, cb) : () => {}),
    [ctx, widgetId, key]
  );
  const getSnapshot = useCallback(() => {
    if (!ctx) return defaultValue;
    const v = ctx.getSnapshot(widgetId, key);
    return v === undefined ? defaultValue : v;
  }, [ctx, widgetId, key, defaultValue]);

  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setValue = useCallback(
    (next) => {
      if (!ctx) return;
      const cur = ctx.getSnapshot(widgetId, key);
      const resolved = cur === undefined ? defaultValue : cur;
      const v = typeof next === 'function' ? next(resolved) : next;
      ctx.setValue(widgetId, key, v);
    },
    [ctx, widgetId, key, defaultValue]
  );

  return [value, setValue];
}
